import { captureAttribution, getAttribution, getGaClientId, getLastAttribution } from "../attribution";
import { IT_PAGE_PATHS } from "../paths";

// Globalny bootstrap (PT-250) — JEDYNY embed na wszystkich stronach Webflow:
//   <script src="https://webflow.klinikaholi.pl/script.js"></script>  (w <head>, bez defer/async — odpala się wcześnie)
//
// Steruje całością:
//   1. ZAWSZE odpala atrybucję (wbudowaną, bez osobnego requestu).
//   2. Na ścieżkach z STRIPE_PAGES ładuje js.stripe.com — RÓWNOLEGLE z parsowaniem
//      DOM (od razu), a skrypt strony odpala dopiero gdy Stripe gotowy (bo config.ts
//      woła Stripe() na poziomie modułu).
//   3. Doładowuje skrypt strony po ścieżce: `/` → `_`, lowercase
//
// Priorytet: wstrzykiwane skrypty dostają fetchpriority=high — to krytyczne zasoby strony.
// `_`-prefiks skryptów stron (z wiodącego `/`) gwarantuje brak kolizji ze `script.js`.

// Strony, których skrypt importuje `stripe` z `src/stripe.ts` (eager `Stripe()`) —
// tu MUSI być wczytany js.stripe.com przed skryptem strony. Trzymaj zgodne z realnym
// importem `../stripe` (strony importujące tylko `API` z `./config` NIE potrzebują Stripe).
const STRIPE_PAGES = [
  "/rejestracja",
  "/platnosc",
  "/potwierdzenie",
];

const IT_ALIASES: Record<string, string> = Object.fromEntries(
  Object.entries(IT_PAGE_PATHS).map(([pl, it]) => [it, pl]),
);

// Origin skryptu (webflow.klinikaholi.pl / -stage / -ai) — łap przy starcie (currentScript null po DOMContentLoaded).
const SELF = document.currentScript as HTMLScriptElement | null;
const ORIGIN = SELF?.src ? new URL(SELF.src).origin : "https://webflow.klinikaholi.pl";
// Ścieżka bez trailing slasha (dev serwuje `/payment/` → normalizujemy do `/payment`).
const PATH = location.pathname.replace(/\/+$/, "") || "/";
const PAGE_PATH = IT_ALIASES[PATH] ?? PATH;

function inject(src: string, onerror?: () => void, onload?: () => void): void {
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  s.setAttribute("fetchpriority", "high");
  if (onload) s.onload = onload;
  if (onerror) s.onerror = onerror;
  document.head.appendChild(s);
}

const name = PAGE_PATH.replace(/\//g, "_").toLowerCase();
const pageSrc = /^[a-z0-9_-]+$/.test(name) ? `${ORIGIN}/${name}.js` : null;
const needsStripe = STRIPE_PAGES.includes(PAGE_PATH);

let domReady = document.readyState !== "loading";
let stripeReady = !needsStripe;
let started = false;
let attributionCaptured = false;

function runPageScript(): void {
  if (started || !domReady || !stripeReady || !pageSrc) return;
  started = true;
  inject(pageSrc, () => console.log(`[holi] Dla strony ${location.pathname} nie ma skryptu (${name}.js)`));
}

function captureAndNotify(): void {
  if (!captureAttribution()) return;
  attributionCaptured = true;
  window.dispatchEvent(new Event("holi:attribution-updated"));
}

function captureAfterConsent(): void {
  if (!window.Cookiebot?.consent?.statistics) return;
  const start = Date.now();
  const tick = () => {
    if (attributionCaptured) return;
    if (getGaClientId()) captureAndNotify();
    else if (Date.now() - start < 5000) setTimeout(tick, 250);
  };
  tick();
}

export function boot(): void {
  window.holiAttribution = { getAttribution, getLastAttribution };
  captureAndNotify();
  window.addEventListener("CookiebotOnAccept", captureAfterConsent);
  // Stripe równolegle z parsowaniem; gdy gotowy → odblokuj skrypt strony.
  if (needsStripe) {
    const ready = () => { stripeReady = true; runPageScript(); };
    inject("https://js.stripe.com/v3/", ready, ready); // onerror też odblokowuje (skrypt strony zaloguje brak)
  }
  if (!domReady) document.addEventListener("DOMContentLoaded", () => { domReady = true; runPageScript(); });
  runPageScript();
}

boot();
